import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { printClassicReceipt } from "../services/feePaymentService";
import { registerReceiptPrintHandler } from "./receiptPrint";
import { buildReceiptPdf, fetchFeeReceipt, FeeReceiptData, receiptFileName } from "./receiptService";

type PrintMethod = "new" | "classic";

const METHOD_KEY = "msp.receiptPrintMethod";

const readMethod = (): PrintMethod => {
  try {
    return localStorage.getItem(METHOD_KEY) === "classic" ? "classic" : "new";
  } catch {
    return "new";
  }
};

const saveMethod = (method: PrintMethod) => {
  try {
    localStorage.setItem(METHOD_KEY, method);
  } catch {
    // per-browser convenience only
  }
};

const METHODS: { value: PrintMethod; title: string; description: string; icon: JSX.Element }[] = [
  {
    value: "new",
    title: "New receipt",
    description: "Built in your browser. Preview it, then print or download.",
    icon: <ReceiptLongRoundedIcon />,
  },
  {
    value: "classic",
    title: "Classic receipt",
    description: "The current server PDF, downloaded straight away.",
    icon: <CloudDownloadOutlinedIcon />,
  },
];

type Preview =
  | { state: "loading"; receiptNo: number }
  | { state: "ready"; receiptNo: number; receipt: FeeReceiptData; url: string; fileName: string }
  | { state: "error"; receiptNo: number; message: string };

const errorText = (err: any): string => {
  const status = err?.response?.status;
  if (status === 403) return "You don't have access to this receipt.";
  if (status === 404) return "This receipt could not be found.";
  if (!err?.response && err?.isAxiosError) return "Could not reach the server. Check your connection and try again.";
  return "The receipt could not be generated. Please try again.";
};

// Browsers that show PDFs inline (most desktops). Phones usually don't, so they get a download card.
const canPreviewPdf = (): boolean => {
  const nav = navigator as Navigator & { pdfViewerEnabled?: boolean };
  return nav.pdfViewerEnabled ?? true;
};

/** Mounted once in App: asks Classic or New for every receipt print, and shows the new receipt. */
export const ReceiptPrintHost = () => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const [chooseFor, setChooseFor] = useState<number | null>(null);
  const [method, setMethod] = useState<PrintMethod>(readMethod);
  const [preview, setPreview] = useState<Preview | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const requestRef = useRef(0);

  useEffect(() => {
    registerReceiptPrintHandler((receiptNo) => {
      setMethod(readMethod());
      setChooseFor(receiptNo);
    });
    return () => registerReceiptPrintHandler(null);
  }, []);

  // Free the PDF when the preview closes or changes
  const previewUrl = preview?.state === "ready" ? preview.url : null;
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const generate = async (receiptNo: number) => {
    const request = ++requestRef.current;
    setPreview({ state: "loading", receiptNo });
    try {
      const receipt = await fetchFeeReceipt(receiptNo);
      const blob = await buildReceiptPdf(receipt);
      if (request !== requestRef.current) return; // closed or replaced meanwhile
      setPreview({ state: "ready", receiptNo, receipt, url: URL.createObjectURL(blob), fileName: receiptFileName(receipt) });
    } catch (err) {
      if (request !== requestRef.current) return;
      setPreview({ state: "error", receiptNo, message: errorText(err) });
    }
  };

  const proceed = (chosen: PrintMethod = method) => {
    if (chooseFor === null) return;
    const receiptNo = chooseFor;
    saveMethod(chosen);
    setChooseFor(null);
    if (chosen === "classic") printClassicReceipt(receiptNo);
    else generate(receiptNo);
  };

  const closePreview = () => {
    requestRef.current++;
    setPreview(null);
  };

  const download = () => {
    if (preview?.state !== "ready") return;
    const link = document.createElement("a");
    link.href = preview.url;
    link.download = preview.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const print = () => {
    if (preview?.state !== "ready") return;
    try {
      const frameWindow = frameRef.current?.contentWindow;
      if (!frameWindow) throw new Error("No preview frame");
      frameWindow.focus();
      frameWindow.print();
    } catch {
      // Some browsers block printing an embedded PDF; their own viewer can print it
      window.open(preview.url, "_blank", "noopener");
    }
  };

  const ready = preview?.state === "ready" ? preview : null;
  const inlinePreview = canPreviewPdf();

  return (
    <>
      {/* Step 1: which receipt */}
      <Dialog open={chooseFor !== null} onClose={() => setChooseFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Print receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Receipt No. <b>{chooseFor}</b>. Your choice is remembered for next time.
          </Typography>
          <Stack spacing={1.25} role="radiogroup" aria-label="Receipt type">
            {METHODS.map((option) => {
              const selected = option.value === method;
              return (
                <ButtonBase
                  key={option.value}
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setMethod(option.value)}
                  onDoubleClick={() => proceed(option.value)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: 1.75,
                    width: "100%",
                    textAlign: "left",
                    borderRadius: 2,
                    border: 1.5,
                    borderColor: selected ? "primary.main" : "divider",
                    bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
                    transition: "border-color .15s, background-color .15s",
                    "&:hover": { borderColor: selected ? "primary.main" : "text.secondary" },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      width: 38,
                      height: 38,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      color: selected ? "primary.main" : "text.secondary",
                      bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : "action.hover",
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2">{option.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                  <CheckCircleRoundedIcon fontSize="small" sx={{ color: "primary.main", visibility: selected ? "visible" : "hidden", mt: 0.25 }} />
                </ButtonBase>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setChooseFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => proceed()}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step 2 (new receipt): preview, print or download */}
      <Dialog
        open={preview !== null}
        onClose={closePreview}
        fullScreen={isPhone}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: isPhone ? "100%" : "90vh" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <ReceiptLongRoundedIcon color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              Receipt No. {preview?.receiptNo}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {ready ? `${ready.receipt.name} · ${ready.fileName}` : preview?.state === "loading" ? "Preparing receipt…" : " "}
            </Typography>
          </Box>
          {ready && !isPhone && (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<DownloadRoundedIcon />} onClick={download}>
                Download
              </Button>
              {inlinePreview && (
                <Button variant="contained" size="small" startIcon={<PrintRoundedIcon />} onClick={print}>
                  Print
                </Button>
              )}
            </Stack>
          )}
          <Tooltip title="Close">
            <IconButton onClick={closePreview} aria-label="Close" edge="end">
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: "flex", bgcolor: "grey.100" }}>
          {preview?.state === "loading" && (
            <Stack spacing={1.5} sx={{ m: "auto", alignItems: "center" }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Preparing receipt…
              </Typography>
            </Stack>
          )}

          {preview?.state === "error" && (
            <Stack spacing={2} sx={{ m: "auto", p: 3, maxWidth: 420, alignItems: "center" }}>
              <Alert severity="error" sx={{ width: "100%" }}>
                {preview.message}
              </Alert>
              <Button variant="outlined" onClick={() => generate(preview.receiptNo)}>
                Try again
              </Button>
            </Stack>
          )}

          {ready && inlinePreview && (
            <Box
              component="iframe"
              ref={frameRef}
              title={`Receipt ${ready.receiptNo}`}
              src={`${ready.url}#toolbar=0&navpanes=0&view=FitH`}
              sx={{ flex: 1, width: "100%", border: 0 }}
            />
          )}

          {ready && !inlinePreview && (
            <Stack spacing={1.5} sx={{ m: "auto", p: 3, alignItems: "center", textAlign: "center" }}>
              <Box sx={{ display: "grid", placeItems: "center", width: 56, height: 56, borderRadius: "50%", bgcolor: alpha(theme.palette.primary.main, 0.1), color: "primary.main" }}>
                <ReceiptLongRoundedIcon />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Your receipt is ready
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ready.fileName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <Button variant="contained" startIcon={<DownloadRoundedIcon />} onClick={download}>
                  Download
                </Button>
                <Button variant="outlined" startIcon={<OpenInNewRoundedIcon />} onClick={() => window.open(ready.url, "_blank", "noopener")}>
                  Open
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>

        {ready && isPhone && inlinePreview && (
          <Box sx={{ display: "flex", gap: 1, p: 1.5, borderTop: 1, borderColor: "divider" }}>
            <Button fullWidth variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={download}>
              Download
            </Button>
            <Button fullWidth variant="contained" startIcon={<PrintRoundedIcon />} onClick={print}>
              Print
            </Button>
          </Box>
        )}
      </Dialog>
    </>
  );
};
